import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { toast } from "sonner"
import { swivuGridSorterFn } from "@/other/utils/utils";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import { resolveIncentiveEvaluationOutcomeRecords } from "@/modern/components/async-tasks/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys.jsx";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}


/** Formats a timestamp into a short relative label: "3m ago", "2h ago", "5d ago". */
export function formatRelativeTime(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  if (ms < 60_000) return "Just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  if (ms < 30 * 86_400_000) return `${Math.floor(ms / (7 * 86_400_000))}w ago`;
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export const checkGrantOrder = ({ canEdit, canView, form, onViewPermissionError }: {
  canEdit: number;
  canView: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: any,
  onViewPermissionError?: (canEdit: number) => void
}) => {
  if (canEdit > canView) {
    if (form) {
      form.setValue("canView", canEdit);
    } else if (onViewPermissionError) {
      onViewPermissionError(canEdit);
    } else {
      toast.error("View permission cannot be higher than Edit permission");
      return false;
    }
  }
  return true;
};



export const getFileExtension = (filename?: string): string => {
  if (!filename || typeof filename !== "string") return "";
  if (filename.includes("localhost")) return "jpg";
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

export interface SortFinderRecordsOptions<T> {
  currentBoardUserId?: number;
  getUpdatedByField?: (record: T) => number | undefined;
  getUpdatedAtField?: (record: T) => Date | string | number | undefined;
  nameField?: string;
  disableNameSort?: boolean;
  disableUpdatedAtSort?: boolean;
  disableUpdatedBySort?: boolean;
}

export function swivuRecentRecordSortFn<T>(records: T[], options: SortFinderRecordsOptions<T> = {}): T[] {
  const {
    currentBoardUserId,
    getUpdatedByField = (r: T): number | undefined => (r as any).updaterId,
    getUpdatedAtField = (r: T): Date | string | number | undefined => (r as any).updatedAt,
    nameField = "name",
    disableNameSort = false,
    disableUpdatedAtSort = false,
    disableUpdatedBySort = false
  } = options;

  const toTimestamp = (value: Date | string | number | undefined | null): number => (value != null ? new Date(value).getTime() : 0);
  const sortByName = disableNameSort ? undefined : (swivuGridSorterFn.bind({ field: nameField }) as (a: T, b: T) => number);

  const canTwoTierSort = !disableUpdatedBySort && currentBoardUserId != null;
  if (!canTwoTierSort) {
    return sortByName ? [...records].sort(sortByName) : [...records];
  }

  const [userUpdated, others] = records.reduce<[T[], T[]]>(
    ([mine, rest], record) => {
      (getUpdatedByField(record) === currentBoardUserId ? mine : rest).push(record);
      return [mine, rest];
    },
    [[], []]
  );

  if (!disableUpdatedAtSort) {
    userUpdated.sort((a, b) => toTimestamp(getUpdatedAtField(b)) - toTimestamp(getUpdatedAtField(a)));
  }
  if (sortByName) {
    others.sort(sortByName);
  }

  return [...userUpdated, ...others];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const navigateToViewByType = (viewManager: any, viewType: string | number, viewName: string, otherParams: Record<string, unknown> = {}) => {
  if (!viewManager) return;
  const view = viewManager.getDefaultViewByType(viewType);
  const hasOtherParams = Object.keys(otherParams).length > 0;
  if (view) {
    viewManager.selectView(view, { otherParams: hasOtherParams ? otherParams : undefined });
  } else {
    viewManager.openAnonymousViewByType({ viewType, viewName, otherParams });
  }
};

export type OpenExecutedIncentivesEditorArgs = {
  tasks: AsyncTask[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  project: any;
  fetchResult: (taskId: number) => Promise<unknown>;
  markSeen: (taskId: number) => Promise<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorHandler: { openEditor: (...args: any[]) => unknown };
};

/**
 * Resolve outcome records for completed incentive-evaluation tasks and open
 * the executed-incentives editor popup when there is something to show.
 */
export async function openExecutedIncentivesEditor({
  tasks,
  project,
  fetchResult,
  markSeen,
  editorHandler,
}: OpenExecutedIncentivesEditorArgs): Promise<boolean> {
  if (!project || !editorHandler || tasks.length === 0) return false;

  let { records, taskIds, emptyTaskIds } =
    await resolveIncentiveEvaluationOutcomeRecords(tasks, fetchResult, project);

  // Fallback: older/completed tasks may lack points_created in the result payload
  // but still carry incentive_id on meta_data (and a store record).
  if (records.length === 0) {
    const fallbackRecords: NonNullable<
      ReturnType<typeof project.outcomeIncentiveStore.getById>
    >[] = [];
    const fallbackTaskIds: number[] = [];
    const seen = new Set<number>();

    for (const task of tasks) {
      const meta = task.meta_data as { incentive_id?: number } | undefined;
      const incentiveId = meta?.incentive_id;
      if (incentiveId == null || seen.has(incentiveId)) continue;
      const record = project.outcomeIncentiveStore?.getById?.(incentiveId);
      if (!record) continue;
      seen.add(incentiveId);
      fallbackRecords.push(record);
      fallbackTaskIds.push(task.id);
    }

    records = fallbackRecords;
    taskIds = fallbackTaskIds;
    emptyTaskIds = emptyTaskIds.filter((id) => !fallbackTaskIds.includes(id));
  }

  for (const taskId of emptyTaskIds) {
    try {
      await markSeen(taskId);
    } catch {
      // Best-effort — task may already be marked seen.
    }
  }

  if (records.length === 0 || taskIds.length === 0) return false;

  editorHandler.openEditor(
    EDITOR_KEYS.executedIncentivesPopup,
    { id: "executed-incentives" },
    { incentiveRecords: records, taskIds },
    { useExisting: true, by: "recordType" },
  );
  return true;
}
